// CP-7814: per-person read status for the Message Status panel.
//
// A participant's last_read_msg_idx moves for every member of a shared-read-receipt group
// (shared RBAC staff) as soon as one of them reads, so on its own it shows the whole group as
// having read a message. The backend's per-message receipts
// (GET chat/message_read_receipts/<conversation>/?message_idx=N) say who opened it themselves.

export const READ = 'read';
export const SHARED = 'shared'; // marked read because a teammate read it
export const UNREAD = 'unread';

// Response -> {msgIdx, byUserId: {id: {readAt, readPersonally}}}, or null when the message has
// no stored receipts (callers then fall back to read horizons).
export const indexReceipts = (data) => {
    if (!data || !data.found || !Array.isArray(data.receipts)) {
        return null;
    }
    const byUserId = {};
    for (const r of data.receipts) {
        byUserId[r.user_id] = {
            readAt: r.read_at || null,
            readPersonally: !!r.read_personally,
            readViaName: r.read_via_name || null, // the teammate whose read marked this one read
            readViaMarkAll: !!r.read_via_mark_all, // "Mark all read" did it (readViaName = who clicked, if not them)
        };
    }
    return { msgIdx: data.message_idx, byUserId };
};

// {status, readAt, readViaName, readViaMarkAll} for one participant and the selected message. receipts is
// indexReceipts() output for that message, or null while loading / when there are none.
export const participantReadStatus = (participant, msgIdx, receipts) => {
    const horizonRead = participant.last_read_msg_idx != null && participant.last_read_msg_idx >= msgIdx;
    const receipt = receipts && receipts.msgIdx === msgIdx ? receipts.byUserId[participant.id] : undefined;
    if (receipt) {
        return receipt.readPersonally
            ? { status: READ, readAt: receipt.readAt, readViaName: null, readViaMarkAll: false }
            : { status: SHARED, readAt: receipt.readAt, readViaName: receipt.readViaName, readViaMarkAll: receipt.readViaMarkAll };
    }
    // No receipt row: trust the horizon (receipts not loaded yet, or a read path that left none)
    return { status: horizonRead ? READ : UNREAD, readAt: null, readViaName: null, readViaMarkAll: false };
};

export const STATUS_RANK = { [READ]: 0, [SHARED]: 1, [UNREAD]: 2 };

// "Oct 14, 3:02 PM" in the viewer's zone; the year only when it is not this year.
export const formatReadAt = (iso, now = new Date()) => {
    if (!iso) {
        return '';
    }
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        return '';
    }
    const opts = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    if (d.getFullYear() !== now.getFullYear()) {
        opts.year = 'numeric';
    }
    return d.toLocaleString(undefined, opts);
};

// readViaName is unknown for receipts the backfill could not match (or a since-deleted user)
export const readAtText = ({ status, readAt, readViaName, readViaMarkAll }) => {
    if (status === UNREAD || !readAt) {
        return '';
    }
    const when = formatReadAt(readAt);
    if (!when) {
        return '';
    }
    if (status !== SHARED) {
        return `Read ${when}`;
    }
    if (readViaMarkAll) {
        // Mark all read clears the unread count without anyone opening the message
        return readViaName ? `Marked read ${when} (${readViaName} used Mark all read)` : `Marked read ${when} (Mark all read)`;
    }
    return `Marked read ${when} (${readViaName || 'a teammate'} read it)`;
};
